import React, { useMemo, useState } from 'react';

/**
 * The one table the Reporting tab's breakdowns are built from.
 *
 * Every tab shows a different set of columns over a different row type, but
 * they all want the same three behaviours — sort on any header, right-align
 * numbers, render an empty state instead of a bare header — and before this
 * existed each table re-implemented its own CSS grid and its own header row.
 * That is how By data body and By client drifted into different column
 * paddings. One component, one grid, one sort story.
 *
 * Sorting is CLIENT-SIDE on purpose. These tables are one row per data body
 * or per delivery destination — tens of rows, not thousands — so a round
 * trip per header click would add latency and a cache key for nothing. If a
 * tab ever needs to page, sorting moves to the server with it.
 *
 * `sortValue` is what decides order; `render` is what the user sees. Keeping
 * them separate is what lets a column sort on a raw number while rendering
 * "1.2M", and sort a rate numerically while rendering an em dash for null.
 */

export interface Column<T> {
  /** Stable key — also the sort identity, so it must be unique per table. */
  key: string;
  label: string;
  /** Grid track for this column, e.g. '1fr' or '110px'. */
  width: string;
  align?: 'left' | 'right';
  /**
   * Value to sort on. Omit to make the column unsortable (the Drill link).
   * Nulls always sink to the bottom regardless of direction — a row with no
   * rate is not "worse than 0%", it is unmeasured.
   */
  sortValue?: (row: T) => number | string | null;
  render: (row: T) => React.ReactNode;
  /** Tooltip on the header — the place to explain a rate's definition. */
  title?: string;
}

type Direction = 'asc' | 'desc';

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Column key to sort by on first render. */
  defaultSort?: string;
  defaultDirection?: Direction;
  /** Shown in place of the table when there are no rows at all. */
  emptyMessage: string;
}

function compare(a: number | string | null, b: number | string | null): number {
  // Nulls sink, always — see the sortValue note above.
  if (a === null && b === null) {return 0;}
  if (a === null) {return 1;}
  if (b === null) {return -1;}
  if (typeof a === 'number' && typeof b === 'number') {return a - b;}
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}

export default function SortableTable<T>({
  columns,
  rows,
  rowKey,
  defaultSort,
  defaultDirection = 'desc',
  emptyMessage,
}: Props<T>) {
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSort);
  const [direction, setDirection] = useState<Direction>(defaultDirection);

  const sorted = useMemo(() => {
    const col = columns.find(c => c.key === sortKey);
    if (!col?.sortValue) {return rows;}
    const getValue = col.sortValue;
    const factor = direction === 'asc' ? 1 : -1;
    // Copy before sorting — the caller's array is React state upstream.
    return [...rows].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      // Null-sinking must not be flipped by direction, so apply the factor
      // only when both sides are present.
      if (av === null || bv === null) {return compare(av, bv);}
      return compare(av, bv) * factor;
    });
  }, [rows, columns, sortKey, direction]);

  if (rows.length === 0) {
    return (
      <div style={{
        padding: 24,
        background: '#f9fafb',
        border: '1px dashed #e5e7eb',
        borderRadius: 8,
        color: '#9ca3af',
        textAlign: 'center',
      }}>
        {emptyMessage}
      </div>
    );
  }

  const template = columns.map(c => c.width).join(' ');

  const toggle = (col: Column<T>) => {
    if (!col.sortValue) {return;}
    if (col.key === sortKey) {
      setDirection(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(col.key);
      // A new numeric column almost always wants "biggest first"; a fresh
      // ascending sort on volume is never what anyone means by one click.
      setDirection('desc');
    }
  };

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      overflowX: 'auto',
    }}>
      <div style={{ minWidth: 720 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: template,
          padding: '10px 16px',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          fontSize: 11,
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}>
          {columns.map(col => {
            const sortable = !!col.sortValue;
            const active = sortable && col.key === sortKey;
            return (
              <div
                key={col.key}
                title={col.title}
                onClick={() => toggle(col)}
                role={sortable ? 'button' : undefined}
                tabIndex={sortable ? 0 : undefined}
                onKeyDown={e => {
                  if (sortable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    toggle(col);
                  }
                }}
                style={{
                  textAlign: col.align === 'right' ? 'right' : 'left',
                  cursor: sortable ? 'pointer' : 'default',
                  color: active ? '#2563eb' : undefined,
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {col.label}
                {active && (
                  <span style={{ marginLeft: 4 }}>{direction === 'desc' ? '↓' : '↑'}</span>
                )}
              </div>
            );
          })}
        </div>

        {sorted.map(row => (
          <div
            key={rowKey(row)}
            style={{
              display: 'grid',
              gridTemplateColumns: template,
              padding: '12px 16px',
              borderTop: '1px solid #f3f4f6',
              alignItems: 'center',
              fontSize: 14,
            }}
          >
            {columns.map(col => (
              <div
                key={col.key}
                style={{
                  textAlign: col.align === 'right' ? 'right' : 'left',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {col.render(row)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
